import api from './client'
import type { Article, ArticleCategory, ArticleComment, ArticlePayload, ArticleVersion, HelpArticleHealthResponse, HelpKnowledgeGapsFilters, HelpKnowledgeGapsResponse, HelpKnownIssue, PaginatedResponse, TicketArticleSuggestion, TicketArticleSuggestionOutcome, User } from '../api'

export const helpApi = {
  listArticles: async (params?: Record<string, string | number | undefined>): Promise<PaginatedResponse<Article>> => {
    const response = await api.get<PaginatedResponse<Article>>('/help/articles', { params })
    return response.data
  },

  getArticle: async (slug: string): Promise<Article> => {
    const response = await api.get<Article>(`/help/articles/${slug}`)
    return response.data
  },

  createArticle: async (payload: ArticlePayload): Promise<Article> => {
    const response = await api.post<Article>('/help/articles', payload)
    return response.data
  },

  updateArticle: async (slug: string, payload: Partial<ArticlePayload>): Promise<Article> => {
    const response = await api.patch<Article>(`/help/articles/${slug}`, payload)
    return response.data
  },

  submitArticle: async (slug: string): Promise<Article> => {
    const response = await api.post<Article>(`/help/articles/${slug}/submit`)
    return response.data
  },

  publishArticle: async (slug: string): Promise<Article> => {
    const response = await api.post<Article>(`/help/articles/${slug}/publish`)
    return response.data
  },

  rejectArticle: async (slug: string, rejection_note: string): Promise<Article> => {
    const response = await api.post<Article>(`/help/articles/${slug}/reject`, { rejection_note })
    return response.data
  },

  archiveArticle: async (slug: string): Promise<Article> => {
    const response = await api.post<Article>(`/help/articles/${slug}/archive`)
    return response.data
  },

  restoreArticle: async (slug: string): Promise<Article> => {
    const response = await api.post<Article>(`/help/articles/${slug}/restore`)
    return response.data
  },

  deleteArticle: async (slug: string): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/help/articles/${slug}`)
    return response.data
  },

  listVersions: async (slug: string): Promise<ArticleVersion[]> => {
    const response = await api.get<ArticleVersion[]>(`/help/articles/${slug}/versions`)
    return response.data
  },

  getVersion: async (slug: string, versionNumber: number): Promise<ArticleVersion> => {
    const response = await api.get<ArticleVersion>(`/help/articles/${slug}/versions/${versionNumber}`)
    return response.data
  },

  submitFeedback: async (slug: string, payload: { helpful: boolean; comment?: string | null }): Promise<Article> => {
    const response = await api.post<Article>(`/help/articles/${slug}/feedback`, payload)
    return response.data
  },

  listFeedback: async (slug: string): Promise<Array<{ id: string; helpful: boolean; comment: string | null; user: Pick<User, 'id' | 'name' | 'role'> | null; created_at: string }>> => {
    const response = await api.get(`/help/articles/${slug}/feedback`)
    return response.data
  },

  listComments: async (slug: string): Promise<ArticleComment[]> => {
    const response = await api.get<ArticleComment[]>(`/help/articles/${slug}/comments`)
    return response.data
  },

  postComment: async (slug: string, body: string): Promise<ArticleComment> => {
    const response = await api.post<ArticleComment>(`/help/articles/${slug}/comments`, { body })
    return response.data
  },

  resolveComment: async (slug: string, commentId: string): Promise<ArticleComment> => {
    const response = await api.post<ArticleComment>(`/help/articles/${slug}/comments/${commentId}/resolve`)
    return response.data
  },

  searchTicketSuggestions: async (payload: { subject?: string; body?: string }): Promise<{ data: TicketArticleSuggestion[] }> => {
    const response = await api.post<{ data: TicketArticleSuggestion[] }>('/help/ticket-suggestions/search', {
      subject: payload.subject ?? '',
      body: payload.body ?? '',
    })
    return response.data
  },

  createTicketSuggestion: async (payload: { article_id?: string; ticket_id?: string }): Promise<TicketArticleSuggestionOutcome> => {
    const response = await api.post<TicketArticleSuggestionOutcome>('/help/ticket-suggestions', payload)
    return response.data
  },

  updateTicketSuggestion: async (id: string, payload: { token: string; article_id?: string; ticket_id?: string }): Promise<TicketArticleSuggestionOutcome> => {
    const response = await api.patch<TicketArticleSuggestionOutcome>(`/help/ticket-suggestions/${id}`, payload)
    return response.data
  },

  gaps: async (params: HelpKnowledgeGapsFilters = {}): Promise<HelpKnowledgeGapsResponse> => {
    const response = await api.get<HelpKnowledgeGapsResponse>('/help/gaps', { params })
    return response.data
  },

  health: async (): Promise<HelpArticleHealthResponse> => {
    const response = await api.get<HelpArticleHealthResponse>('/help/health')
    return response.data
  },

  updateHealthSettings: async (staleAfterDays: number): Promise<{ stale_after_days: number }> => {
    const response = await api.patch<{ stale_after_days: number }>('/help/health/settings', {
      stale_after_days: staleAfterDays,
    })
    return response.data
  },

  knownIssues: async (): Promise<{ data: HelpKnownIssue[] }> => {
    const response = await api.get<{ data: HelpKnownIssue[] }>('/help/known-issues')
    return response.data
  },

  articleKnownIssue: async (slug: string): Promise<{ known_issue: HelpKnownIssue | null }> => {
    const response = await api.get<{ known_issue: HelpKnownIssue | null }>(`/help/articles/${slug}/known-issue`)
    return response.data
  },

  saveKnownIssue: async (slug: string, payload: { starts_at: string; ends_at: string | null }): Promise<{ known_issue: HelpKnownIssue }> => {
    const response = await api.put<{ known_issue: HelpKnownIssue }>(`/help/articles/${slug}/known-issue`, payload)
    return response.data
  },

  clearKnownIssue: async (slug: string): Promise<void> => {
    await api.delete(`/help/articles/${slug}/known-issue`)
  },

  listCategories: async (): Promise<ArticleCategory[]> => {
    const response = await api.get<ArticleCategory[]>('/help/categories')
    return response.data
  },

  createCategory: async (payload: Partial<ArticleCategory>): Promise<ArticleCategory> => {
    const response = await api.post<ArticleCategory>('/help/categories', payload)
    return response.data
  },

  updateCategory: async (id: string, payload: Partial<ArticleCategory>): Promise<ArticleCategory> => {
    const response = await api.patch<ArticleCategory>(`/help/categories/${id}`, payload)
    return response.data
  },

  deleteCategory: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/help/categories/${id}`)
    return response.data
  },
}
